import { apiGet, apiPatch, apiPost } from './api-client.js';

/**
 * Typed wrappers over API_CONTRACT.md §13.10's `/org-units` (I20). Shapes match
 * `api/src/modules/org/transport/org-routes.ts`'s `orgUnitPayload` exactly.
 *
 * No `DELETE` exists on the server (A-006: nothing is hard-deleted) and no re-parent —
 * `PATCH` renames only. This client mirrors that surface exactly rather than inventing
 * either.
 */

export const ORG_UNIT_KINDS = ['ministry', 'division', 'section'] as const;
export type OrgUnitKind = (typeof ORG_UNIT_KINDS)[number];

export interface OrgUnit {
  id: string;
  parentId: string | null;
  kind: OrgUnitKind;
  code: string;
  nameNe: string;
  nameEn: string;
  depth: number;
  archivedAt: string | null;
  version: number;
}

export function listOrgUnits(includeArchived: boolean, signal?: AbortSignal): Promise<OrgUnit[]> {
  const query = includeArchived ? '?includeArchived=true' : '';
  return apiGet<OrgUnit[]>(`/org-units${query}`, signal);
}

export interface CreateOrgUnitInput {
  parentId: string | null;
  kind: OrgUnitKind;
  code: string;
  nameNe: string;
  nameEn: string;
}

export function createOrgUnit(input: CreateOrgUnitInput, signal?: AbortSignal): Promise<OrgUnit> {
  return apiPost<OrgUnit>('/org-units', input, signal);
}

export interface RenameOrgUnitInput {
  code?: string;
  nameNe?: string;
  nameEn?: string;
  expectedVersion: number;
}

export function renameOrgUnit(orgUnitId: string, input: RenameOrgUnitInput, signal?: AbortSignal): Promise<OrgUnit> {
  return apiPatch<OrgUnit>(`/org-units/${orgUnitId}`, input, signal);
}

/**
 * `409 ORG_UNIT_NOT_EMPTY` carries `{liveFiles, activeStaff, liveChildren}` in
 * `error.details` (API_CONTRACT §13.10 amendment 3) — the caller reads that off the
 * thrown `ApiError.details`, not off this function's return type.
 */
export function archiveOrgUnit(orgUnitId: string, signal?: AbortSignal): Promise<OrgUnit> {
  return apiPost<OrgUnit>(`/org-units/${orgUnitId}/archive`, undefined, signal);
}

export { ApiError, NetworkError } from './api-client.js';
